import Datatable from "components/Datatable/Datatable";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { checkNonMoneyDataForTable } from "utils/utils";
import {
  createSubmissionConfiguration,
  querySubmissionList,
  updateSubmissionConfiguration,
} from "./model";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import s from "./SubmissionConfiguration.module.scss";
import c from "lib/style/colorVar.scss";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ButtonMAS from "ButtonMAS/ButtonMAS";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Selection from "components/Input/Selection/Selection";
import submissionBankOptions from "utils/BankSubmissionOptions/SubmissionBankOptions";
import { Typography } from "@mui/material";

const PAGE_LOCALE_KEY =
  "page.finance.bankSubmission.innerTab.submissionConfiguration.";

const INIT_DIALOG_DATA = {
  id: null, //only exist for edit scenarios
  submissionBank: "DBS",
  fileSuffix: null,
  merchantIdList: null,
  description: null,
};

const SubmissionConfiguration = () => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { snackbarSuccess, snackbarError } = useSnackbarMAS();

  const [fetchingList, setFetchingList] = useState(true);
  const [columnsArray, setColumnsArray] = useState([]);
  const [datasource, setDatasource] = useState({
    data: [],
  });

  const [awaitingCreateRes, setAwaitingCreateRes] = useState(false);
  const [awaitingEditRes, setAwaitingEditRes] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [isDialogCreateAction, setIsDialogCreateAction] = useState(false); // dialog is edit mode if false
  const isDialogCreateActionRef = useRef();
  isDialogCreateActionRef.current = isDialogCreateAction;
  const [configDialogData, setConfigDialogDataState] =
    useState(INIT_DIALOG_DATA);
  /**
   * @param {"fileSuffix"|"submissionBank"|"merchantIdList"|"description"} field
   * @param {*} data
   */
  const setConfigDialogData = (field, data) => {
    setConfigDialogDataState((prev) => ({ ...prev, [field]: data }));
  };

  const configDialogInvalidFields = useMemo(() => {
    const verifyMerchantIdListFormat = () => {
      if (!configDialogData.merchantIdList) return true;
      return /^[a-zA-Z0-9]+(,\s*[a-zA-Z0-9]+)*,?\s*$/.test(
        configDialogData.merchantIdList,
      );
    };
    const verifyMerchantIdListUnique = () => {
      if (!configDialogData.merchantIdList) return true;
      const arrayList = configDialogData.merchantIdList
        .split(",")
        .map((merchantId) => merchantId.trim())
        .filter(Boolean);
      return arrayList.length === new Set(arrayList).size;
    };
    return {
      submissionBank: !configDialogData.submissionBank,
      fileSuffix: !configDialogData.fileSuffix,
      fileSuffixMessage:
        configDialogData.fileSuffix !== null && !configDialogData.fileSuffix,
      fileSuffixInvalidFormat:
        configDialogData.fileSuffix &&
        !/^[0-9a-zA-Z!\-_.\*'()]+$/.test(configDialogData.fileSuffix),
      merchantIdList: !configDialogData.merchantIdList,
      merchantIdListMessage:
        configDialogData.merchantIdList !== null &&
        !configDialogData.merchantIdList,
      merchantIdListFormat:
        configDialogData.merchantIdList !== null &&
        !verifyMerchantIdListFormat(),
      merchantIdListDuplicate:
        configDialogData.merchantIdList !== null &&
        !verifyMerchantIdListUnique(),
    };
  }, [configDialogData]);

  const [awaitingDeleteRes, setAwaitingDeleteRes] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogData, setDeleteDialogData] = useState(INIT_DIALOG_DATA);

  const openConfigDialog = (data) => {
    data && setAwaitingEditRes(false);
    setConfigDialogDataState(data || INIT_DIALOG_DATA);
    //Create Bank Submission Config if no data provided
    setIsDialogCreateAction(!data);
    setConfigDialogOpen(true);
  };

  const openDeleteDialog = (data) => {
    setAwaitingDeleteRes(false);
    setDeleteDialogData(data);
    setDeleteDialogOpen(true);
  };

  useEffect(() => {
    fetchSubmissionConfiguration();
  }, []);

  const fetchSubmissionConfiguration = async () => {
    setFetchingList(true);
    const apiResponse = await querySubmissionList();
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({ message: t("general.message.networkError") });
      setFetchingList(false);
      return;
    }

    switch (apiResponse?.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbarError({ message: t("general.message.unauthorized") });
        break;
      }
      default: {
        break;
      }
    }

    const responseBody = apiResponse?.data;
    switch (responseBody?.responseCode) {
      case "0000": {
        setDatasource(responseBody);
        break;
      }
      default: {
        snackbarError({ message: t("general.message.initial.fetch.error") });
        break;
      }
    }
    setFetchingList(false);
  };

  const handleCreateEditSubmissionConfiguration = async () => {
    if (Object.values(configDialogInvalidFields).some(Boolean)) return;
    const isDialogCreateActionFlag = isDialogCreateAction;
    isDialogCreateActionFlag
      ? setAwaitingCreateRes(true)
      : setAwaitingEditRes(true);

    const payload = {
      submissionBank: configDialogData.submissionBank,
      fileSuffix: configDialogData.fileSuffix,
      merchantIdList: configDialogData.merchantIdList
        ?.split(",")
        ?.map((merchantId) => merchantId.trim())
        ?.filter(Boolean),
      ...(configDialogData.description && {
        description: configDialogData.description,
      }),
    };

    const apiResponse = await (isDialogCreateActionFlag
      ? createSubmissionConfiguration(payload)
      : updateSubmissionConfiguration(configDialogData.id, payload));
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({ message: t("general.message.networkError") });
      isDialogCreateActionFlag
        ? setAwaitingCreateRes(false)
        : setAwaitingEditRes(false);
      isDialogCreateActionFlag === isDialogCreateActionRef.current &&
        setConfigDialogOpen(false);
      fetchSubmissionConfiguration();
      return;
    }

    switch (apiResponse?.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbarError({ message: t("general.message.unauthorized") });
        break;
      }
      default: {
        break;
      }
    }

    const responseBody = apiResponse?.data;
    switch (responseBody?.responseCode) {
      case "0000": {
        isDialogCreateActionFlag
          ? snackbarSuccess({
              message: t(PAGE_LOCALE_KEY + "configDialog.toast.createSuccess"),
            })
          : snackbarSuccess({
              message: t(PAGE_LOCALE_KEY + "configDialog.toast.updateSuccess"),
            });
        break;
      }
      default: {
        const failureMessage = isDialogCreateActionFlag
          ? t(PAGE_LOCALE_KEY + "configDialog.toast.createFail")
          : t(PAGE_LOCALE_KEY + "configDialog.toast.updateFail");

        snackbarError({
          message: [responseBody?.message, failureMessage]
            .filter(Boolean)
            .join(", "),
        });
        break;
      }
    }
    isDialogCreateActionFlag
      ? setAwaitingCreateRes(false)
      : setAwaitingEditRes(false);
    isDialogCreateActionFlag === isDialogCreateActionRef.current &&
      setConfigDialogOpen(false);
    fetchSubmissionConfiguration();
  };

  const handleDeleteSubmissionConfiguration = async () => {
    setAwaitingDeleteRes(true);

    const payload = {
      submissionBank: deleteDialogData.submissionBank,
      fileSuffix: deleteDialogData.fileSuffix,
      merchantIdList: deleteDialogData.merchantIdList
        ?.split(",")
        ?.map((merchantId) => merchantId.trim())
        ?.filter(Boolean),
      ...(deleteDialogData.description && {
        description: deleteDialogData.description,
      }),
      status: "DELETED",
    };

    const apiResponse = await updateSubmissionConfiguration(
      deleteDialogData.id,
      payload,
    );
    if (!apiResponse || !apiResponse?.data) {
      snackbarError({ message: t("general.message.networkError") });
      setAwaitingDeleteRes(false);
      setDeleteDialogOpen(false);
      fetchSubmissionConfiguration();
      return;
    }

    switch (apiResponse?.status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        snackbarError({ message: t("general.message.unauthorized") });
        break;
      }
      default: {
        break;
      }
    }

    const responseBody = apiResponse?.data;
    switch (responseBody?.responseCode) {
      case "0000": {
        snackbarSuccess({
          message: t(PAGE_LOCALE_KEY + "deleteDialog.toast.deleteSuccess"),
        });
        break;
      }
      default: {
        snackbarError({
          message: t(PAGE_LOCALE_KEY + "deleteDialog.toast.deleteFail"),
        });
        break;
      }
    }
    setAwaitingDeleteRes(false);
    setDeleteDialogOpen(false);
    fetchSubmissionConfiguration();
  };

  const columns = [
    {
      index: 0,
      key: "id",
      title: t(PAGE_LOCALE_KEY + "columnTitle.id"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 1,
      key: "fileSuffix",
      title: t(PAGE_LOCALE_KEY + "columnTitle.fileSuffix"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "submissionBank",
      title: t(PAGE_LOCALE_KEY + "columnTitle.submissionBank"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "merchantIdList",
      title: t(PAGE_LOCALE_KEY + "columnTitle.merchantIdList"),
      sorter: false,
      render: (data, key) => {
        return data[key].join(", ") || "-";
      },
    },
    {
      index: 4,
      key: "description",
      title: t(PAGE_LOCALE_KEY + "columnTitle.description"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "actions",
      title: t(PAGE_LOCALE_KEY + "columnTitle.actions"),
      sorter: false,
      render: (data) => {
        return (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div className={s.actionBtnContainer}>
              <ButtonMAS variant="text-only" overlayLoading
                onClick={() => {
                  return openConfigDialog({
                    ...data,
                    merchantIdList: data.merchantIdList.join(", "),
                  });
                }}
              >
                {t(PAGE_LOCALE_KEY + "columnTitle.actionEdit")}
              </ButtonMAS>
            </div>
            <div className={s.actionBtnContainer}>
              <ButtonMAS variant="text-only" overlayLoading
                onClick={() => {
                  return openDeleteDialog({
                    ...data,
                    merchantIdList: data.merchantIdList.join(", "),
                  });
                }}
              >
                {t(PAGE_LOCALE_KEY + "columnTitle.actionDelete")}
              </ButtonMAS>
            </div>
          </div>
        );
      },
    },
  ];

  const dialogContent = () => {
    return (
      <>
        <OutlinedText
          mandatory
          label={t(PAGE_LOCALE_KEY + "configDialog.fieldLabel.fileSuffix")}
          placeholder={t(
            PAGE_LOCALE_KEY + "configDialog.placeholder.fileSuffix",
          )}
          value={configDialogData.fileSuffix}
          onchangeFn={(e) => setConfigDialogData("fileSuffix", e.target.value)}
          errorMessage={
            configDialogInvalidFields.fileSuffixInvalidFormat &&
            t(PAGE_LOCALE_KEY + "configDialog.error.fileSuffixInvalidFormat")
          }
          sxParams={{
            textError:
              configDialogInvalidFields.fileSuffixMessage ||
              configDialogInvalidFields.fileSuffixInvalidFormat,
          }}
        />
        <Selection
          mandatory
          label={t(PAGE_LOCALE_KEY + "configDialog.fieldLabel.submissionBank")}
          selectionValue={configDialogData.submissionBank}
          onchangeFn={(e) =>
            setConfigDialogData("submissionBank", e.target.value)
          }
          options={submissionBankOptions("all")}
        />
        <OutlinedText
          mandatory
          multiline
          label={t(PAGE_LOCALE_KEY + "configDialog.fieldLabel.merchantIdList")}
          placeholder={t(
            PAGE_LOCALE_KEY + "configDialog.placeholder.merchantIdList",
          )}
          value={configDialogData.merchantIdList}
          onchangeFn={(e) =>
            setConfigDialogData("merchantIdList", e.target.value)
          }
          errorMessage={
            (configDialogInvalidFields.merchantIdListMessage && "") ||
            (configDialogInvalidFields.merchantIdListFormat &&
              t(
                PAGE_LOCALE_KEY + "configDialog.error.merchantIdInvalidFormat",
              )) ||
            (configDialogInvalidFields.merchantIdListDuplicate &&
              t(PAGE_LOCALE_KEY + "configDialog.error.merchantIdDuplicate"))
          }
          sxParams={{
            textError:
              configDialogInvalidFields.merchantIdListMessage ||
              configDialogInvalidFields.merchantIdListFormat ||
              configDialogInvalidFields.merchantIdListDuplicate,
          }}
        />
        <OutlinedText
          multiline
          label={t(PAGE_LOCALE_KEY + "configDialog.fieldLabel.description")}
          value={configDialogData.description}
          onchangeFn={(e) => setConfigDialogData("description", e.target.value)}
        />
      </>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        <FunctionalButtonBar
          loading={fetchingList}
          createBtn
          handleCreate={() => openConfigDialog()}
          isCreateLoading={awaitingCreateRes}
          refreshBtn
          handleRefresh={fetchSubmissionConfiguration}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "bank-submission-configuration",
          }}
        />
        <div
          className={[
            s.datatableContainer,
            fetchingList ? s.loadingNow : "",
          ].join(" ")}
        >
          {fetchingList ? (
            <Spin />
          ) : (
            <>
              <Datatable
                columns={columns}
                columnsPreference={columnsArray}
                datasource={datasource.data}
              />
            </>
          )}
        </div>
        <div style={{ marginTop: 16 }}>
          <Typography variant="label" color={c["blackDark666"]}>
            {t(PAGE_LOCALE_KEY + "footer")}
          </Typography>
        </div>
        <SimpleDialog
          open={configDialogOpen}
          dialogOncloseFn={() => setConfigDialogOpen(false)}
          title={
            isDialogCreateAction
              ? t(PAGE_LOCALE_KEY + "configDialog.createTitle")
              : t(PAGE_LOCALE_KEY + "configDialog.editTitle")
          }
          content={dialogContent()}
          confirmFn={() => handleCreateEditSubmissionConfiguration()}
          positiveAction={
            isDialogCreateAction
              ? t("component.simple.dialog.actionCreate")
              : t("component.simple.dialog.action.3")
          }
          positiveActionDisabled={Object.values(configDialogInvalidFields).some(
            Boolean,
          )}
          positiveActionLoading={
            isDialogCreateAction ? awaitingCreateRes : awaitingEditRes
          }
        />
        <SimpleDialog
          open={deleteDialogOpen}
          dialogOncloseFn={() => setDeleteDialogOpen(false)}
          noTitle
          content={t(PAGE_LOCALE_KEY + "deleteDialog.content")}
          confirmFn={() => handleDeleteSubmissionConfiguration()}
          negativeAction={t("component.simple.dialog.action.2")}
          negativeActionDisabled={awaitingDeleteRes}
          positiveAction={t("component.simple.dialog.action.1")}
          positiveActionLoading={awaitingDeleteRes}
        />
      </div>
    </div>
  );
};

export default SubmissionConfiguration;
